import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSession } from "../Login/SessionContext";
import { API_HOST } from "../../constants";
import { getCookie } from "../../utils/cookieUtil";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import LoadingAnime from "../Common/LoadingAnime";
import Button from "../ui/Button";
import TagInput from "./TagInput";

function BbsEdit() {
  const { isLoading, user } = useSession();
  const { moduleName, document_id } = useParams();
  const navigate = useNavigate();

  const quillRef = useRef(null);

  const [module, setModule] = useState({});
  const [categories, setCategories] = useState([]);
  const [editPost, setEditPost] = useState({
    title: "",
    category: "",
    content: "",
  });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const moduleRes = await axios.get(`${API_HOST}/api/modules/`, {
          params: { module_name: moduleName },
        });

        if (!moduleRes.data.length) {
          setError("게시판 모듈을 찾을 수 없습니다.");
          return;
        }

        const moduleData = moduleRes.data[0];
        setModule(moduleData);

        const [categoriesRes, postRes] = await Promise.all([
          axios.get(`${API_HOST}/api/bbs/${moduleName}/categories/`),
          axios.get(`${API_HOST}/api/bbs/${moduleName}/${document_id}/`),
        ]);

        setCategories(categoriesRes.data);

        const { title, category, content, tags } = postRes.data;

        setEditPost({
          title,
          category,
          content: content || "",
        });

        if (tags) {
          setTags(tags.map((t) => t.name));
        }
      } catch {
        setError("데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [moduleName, document_id]);

  // ✅ 이미지 업로드
  const handleImageUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const csrfToken = getCookie("csrftoken");

      const res = await axios.post(`${API_HOST}/api/upload/`, formData, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      return `${API_HOST}${res.data.imageUrl}`;
    } catch {
      setError("이미지 업로드 실패");
      return null;
    }
  }, []);

  // ✅ 이미지 핸들러 (커서 이동 버그까지 해결)
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const range = editor.getSelection(true);

      const imageUrl = await handleImageUpload(file);
      if (!imageUrl) return;

      editor.insertEmbed(range.index, "image", imageUrl);
      editor.setSelection(range.index + 1); // ⭐ 커서 이동 중요
    };
  }, [handleImageUpload]);

  // ⭐ useMemo 제거 (강력 추천)
  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image: imageHandler,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const handleUpdatePost = async () => {
    const { title, category, content } = editPost;

    if (!title || !content) {
      alert("제목과 내용을 입력해주세요.");
      return;
    }

    try {
      const csrfToken = getCookie("csrftoken");

      const res = await axios.put(
        `${API_HOST}/api/bbs/${document_id}/`,
        {
          title,
          category,
          author: user.id,
          board_module: module.id,
          content,
          tags,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );

      alert("게시물이 수정되었습니다.");
      navigate(`/bbs/${moduleName}/${res?.data?.document_id || document_id}`);
    } catch (err) {
      console.error(err);
      setError("게시물 수정 실패");
    }
  };

  if (isLoading || loading) {
    return <LoadingAnime label="게시글 수정 준비 중..." />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <section className="glass-card p-4 text-sm text-red-200">
          {error}
        </section>
      )}

      <section className="glass-card p-6 space-y-4">
        <select
          className="soft-input"
          value={editPost.category}
          onChange={(e) =>
            setEditPost((prev) => ({ ...prev, category: e.target.value }))
          }
        >
          <option value="">카테고리 선택</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <input
          className="soft-input"
          placeholder="제목"
          value={editPost.title}
          onChange={(e) =>
            setEditPost((prev) => ({ ...prev, title: e.target.value }))
          }
        />

        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editPost.content}
          onChange={(content) =>
            setEditPost((prev) => ({ ...prev, content }))
          }
          modules={modules}
          formats={formats}
        />

        <TagInput value={tags} onChange={setTags} />

        <div className="flex justify-end">
          <Button onClick={handleUpdatePost}>수정</Button>
        </div>
      </section>
    </div>
  );
}

export default BbsEdit;